import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = '/api';

/* ─── Utility: Download CSV ─── */
const downloadCSV = (rows, columns, filename) => {
  if (!rows.length) return;
  const header = columns.map(c => c.label).join(',');
  const body = rows.map(r =>
    columns.map(c => {
      let val = r[c.key] ?? '';
      val = String(val).replace(/"/g, '""');
      return val.includes(',') || val.includes('"') || val.includes('\n') ? `"${val}"` : val;
    }).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`; a.click();
  URL.revokeObjectURL(url);
};

/* ─── Utility: Download PDF (HTML-based) ─── */
const downloadPDF = (rows, columns, title) => {
  if (!rows.length) return;
  const tableRows = rows.map(r =>
    `<tr>${columns.map(c => `<td style="border:1px solid #cbd5e1;padding:8px 12px;font-size:13px;color:#334155">${r[c.key] ?? ''}</td>`).join('')}</tr>`
  ).join('');
  const html = `<!DOCTYPE html><html><head><title>${title}</title><style>
    body{font-family:system-ui,-apple-system,sans-serif;padding:40px;color:#1e293b}
    h1{font-size:22px;margin-bottom:4px} .sub{color:#64748b;font-size:13px;margin-bottom:24px}
    table{width:100%;border-collapse:collapse}
    th{background:#1e293b;color:#fff;padding:10px 12px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
    tr:nth-child(even) td{background:#f8fafc}
    .footer{margin-top:24px;font-size:11px;color:#94a3b8;text-align:center}
  </style></head><body>
    <h1>${title}</h1>
    <div class="sub">Generated on ${new Date().toLocaleString()} &bull; Total Records: ${rows.length}</div>
    <table><thead><tr>${columns.map(c => `<th>${c.label}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table>
    <div class="footer">RCMAS RE-LEARNING PLATFORM &mdash; Admin Report</div>
  </body></html>`;
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.print(); }, 400);
};

/* ─── Report Definitions ─── */
const REPORT_TYPES = {
  faculty: {
    label: 'Faculty Report',
    icon: '👨‍🏫',
    color: '#10b981',
    endpoint: '/reports/faculty',
    defaultSort: { key: 'department', direction: 'asc' },
    statusKey: null,
    dateKey: 'join_date',
    columns: [
      { key: 'fac_id', label: 'Faculty ID', sortable: true },
      { key: 'fac_name', label: 'Name', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      { key: 'phone', label: 'Phone', sortable: false },
      { key: 'designation', label: 'Designation', sortable: true },
      { key: 'department', label: 'Department', sortable: true },
      { key: 'role', label: 'Role', sortable: true },
      { key: 'join_date', label: 'Join Date', sortable: true },
      { key: 'courses_count', label: 'Courses', sortable: true },
    ],
  },
  student: {
    label: 'Student Report',
    icon: '👩‍🎓',
    color: '#3b82f6',
    endpoint: '/reports/students',
    defaultSort: { key: 'status', direction: 'asc' },
    statusKey: 'status',
    statusOptions: ['active', 'inactive'],
    dateKey: 'admitted_date',
    columns: [
      { key: 'std_id', label: 'Student ID', sortable: true },
      { key: 'std_name', label: 'Name', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      { key: 'phone', label: 'Phone', sortable: false },
      { key: 'roll_no', label: 'Roll No', sortable: true },
      { key: 'program', label: 'Program', sortable: true },
      { key: 'batch', label: 'Batch', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'admitted_date', label: 'Admitted Date', sortable: true },
      { key: 'enrollments_count', label: 'Enrollments', sortable: true },
    ],
  },
  course: {
    label: 'Course Report',
    icon: '📚',
    color: '#f59e0b',
    endpoint: '/reports/courses',
    defaultSort: { key: 'status', direction: 'asc' },
    statusKey: 'status',
    statusOptions: ['active', 'inactive'],
    dateKey: 'created_at',
    columns: [
      { key: 'course_id', label: 'Course ID', sortable: true },
      { key: 'course_code', label: 'Code', sortable: true },
      { key: 'course_name', label: 'Course Name', sortable: true },
      { key: 'faculty', label: 'Faculty', sortable: true },
      { key: 'program', label: 'Program', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'publish_status', label: 'Published', sortable: true },
      { key: 'difficulty_level', label: 'Level', sortable: true },
      { key: 'duration_weeks', label: 'Weeks', sortable: true },
      { key: 'enrollment_count', label: 'Enrollments', sortable: true },
      { key: 'created_at', label: 'Created', sortable: true },
    ],
  },
  enrollment: {
    label: 'Enrollment Report',
    icon: '📈',
    color: '#ef4444',
    endpoint: '/reports/enrollments',
    defaultSort: { key: 'status', direction: 'asc' },
    statusKey: 'status',
    statusOptions: ['active', 'completed', 'dropped'],
    dateKey: 'enrollment_date',
    columns: [
      { key: 'enrollment_id', label: 'Enrollment ID', sortable: true },
      { key: 'student_name', label: 'Student', sortable: true },
      { key: 'student_id', label: 'Student ID', sortable: true },
      { key: 'course_name', label: 'Course', sortable: true },
      { key: 'course_code', label: 'Course Code', sortable: true },
      { key: 'enrollment_date', label: 'Enrolled Date', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'faculty', label: 'Faculty', sortable: true },
      { key: 'program', label: 'Program', sortable: true },
    ],
  },
};

/* ─── Main Component ─── */
const AdminReports = () => {
  const [activeReport, setActiveReport] = useState('faculty');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateSort, setDateSort] = useState('none'); // 'none', 'newest', 'oldest'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  const report = REPORT_TYPES[activeReport];

  const fetchReport = useCallback(async (type) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await axios.get(`${API}${REPORT_TYPES[type].endpoint}`, {
        params,
        timeout: 15000,
      });
      setData(res.data || []);
    } catch (err) {
      console.error('Report fetch error:', err);
      setError(err?.response?.data?.error || 'Failed to load report data. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setSearchTerm('');
    setSortConfig({ key: null, direction: 'asc' });
    setStatusFilter('all');
    setDateSort('none');
    setCurrentPage(1);
  }, [activeReport]);

  useEffect(() => {
    fetchReport(activeReport);
  }, [activeReport, fetchReport]);

  /* Sorting */
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  /* Filtered + Sorted data */
  const processedData = useMemo(() => {
    let result = [...data];

    // Date range filter (frontend fallback; backend also filters by date range).
    if ((startDate || endDate) && report.dateKey) {
      result = result.filter((row) => {
        const raw = row[report.dateKey];
        if (!raw || raw === 'â€”') return false;

        const rowDate = new Date(raw);
        if (Number.isNaN(rowDate.getTime())) return false;

        if (startDate) {
          const start = new Date(`${startDate}T00:00:00`);
          if (rowDate < start) return false;
        }
        if (endDate) {
          const end = new Date(`${endDate}T23:59:59`);
          if (rowDate > end) return false;
        }

        return true;
      });
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(row =>
        report.columns.some(col => String(row[col.key] ?? '').toLowerCase().includes(term))
      );
    }

    // Status filter (for student, course, enrollment)
    if (statusFilter !== 'all' && report.statusKey) {
      result = result.filter(row =>
        String(row[report.statusKey] ?? '').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Determine active sort: user click > date toggle > default
    let activeSort = null;
    if (sortConfig.key) {
      activeSort = sortConfig;
    } else if (dateSort !== 'none' && report.dateKey) {
      activeSort = { key: report.dateKey, direction: dateSort === 'newest' ? 'desc' : 'asc' };
    } else if (report.defaultSort) {
      activeSort = report.defaultSort;
    }

    if (activeSort) {
      result.sort((a, b) => {
        const aVal = a[activeSort.key] ?? '';
        const bVal = b[activeSort.key] ?? '';
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
          return activeSort.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        return activeSort.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      });
    }

    return result;
  }, [data, searchTerm, sortConfig, statusFilter, dateSort, report, startDate, endDate]);

  /* Pagination */
  const totalPages = Math.ceil(processedData.length / rowsPerPage);
  const paginatedData = processedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, sortConfig, statusFilter, dateSort, startDate, endDate]);

  return (
    <div style={styles.container}>
      {/* Report Type Selector Cards */}
      <div style={styles.selectorGrid}>
        {Object.entries(REPORT_TYPES).map(([key, rpt]) => (
          <button
            key={key}
            onClick={() => setActiveReport(key)}
            style={{
              ...styles.selectorCard,
              borderColor: activeReport === key ? rpt.color : '#e2e8f0',
              background: activeReport === key ? `${rpt.color}08` : '#fff',
              boxShadow: activeReport === key ? `0 4px 16px ${rpt.color}25` : '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <span style={{ fontSize: '28px' }}>{rpt.icon}</span>
            <span style={{
              fontSize: '13px',
              fontWeight: activeReport === key ? '700' : '500',
              color: activeReport === key ? rpt.color : '#64748b',
              marginTop: '4px',
            }}>{rpt.label}</span>
          </button>
        ))}
      </div>

      {/* Report Panel */}
      <div style={styles.panel}>
        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.toolbarLeft}>
            <h2 style={styles.panelTitle}>
              <span style={{ marginRight: '8px' }}>{report.icon}</span>
              {report.label}
            </h2>
            <span style={styles.recordCount}>{processedData.length} records</span>
          </div>
          <div style={styles.toolbarRight}>
            {/* Status filter dropdown — shown for student, course, enrollment */}
            {report.statusKey && report.statusOptions && (
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setSortConfig({ key: null, direction: 'asc' }); }}
                style={styles.filterSelect}
              >
                <option value="all">All Status</option>
                {report.statusOptions.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            )}
            {/* Date sort toggle */}
            {report.dateKey && (
              <select
                value={dateSort}
                onChange={(e) => { setDateSort(e.target.value); setSortConfig({ key: null, direction: 'asc' }); }}
                style={styles.filterSelect}
              >
                <option value="none">Date: Default</option>
                <option value="newest">Date: Newest First</option>
                <option value="oldest">Date: Oldest First</option>
              </select>
            )}
            {report.dateKey && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={styles.filterSelect}
                  title="Start date"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={styles.filterSelect}
                  title="End date"
                />
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    style={styles.clearRangeBtn}
                    title="Clear date range"
                  >
                    Clear Dates
                  </button>
                )}
              </>
            )}
            <div style={styles.searchBox}>
              <span style={styles.searchIcon}>🔍</span>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} style={styles.clearBtn}>✕</button>
              )}
            </div>
            <button
              onClick={() => downloadCSV(processedData, report.columns, `${activeReport}_report`)}
              style={{ ...styles.actionBtn, background: '#16a34a', color: '#fff' }}
              disabled={!processedData.length}
              title="Download CSV"
            >
              📥 CSV
            </button>
            <button
              onClick={() => downloadPDF(processedData, report.columns, report.label)}
              style={{ ...styles.actionBtn, background: '#dc2626', color: '#fff' }}
              disabled={!processedData.length}
              title="Print / Save as PDF"
            >
              🖨️ PDF
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner} />
            <span>Loading report data...</span>
          </div>
        ) : error ? (
          <div style={styles.errorState}>
            <span style={{ fontSize: '40px' }}>⚠️</span>
            <p>{error}</p>
            <button onClick={() => fetchReport(activeReport)} style={styles.retryBtn}>Retry</button>
          </div>
        ) : processedData.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={{ fontSize: '48px', opacity: 0.4 }}>📋</span>
            <p style={{ color: '#94a3b8', marginTop: '12px' }}>
              {searchTerm ? 'No records match your search.' : 'No data available for this report.'}
            </p>
          </div>
        ) : (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.thIndex}>#</th>
                    {report.columns.map(col => (
                      <th
                        key={col.key}
                        style={{
                          ...styles.th,
                          cursor: col.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                        onClick={() => col.sortable && handleSort(col.key)}
                      >
                        <span style={styles.thContent}>
                          {col.label}
                          {col.sortable && (
                            <span style={styles.sortIndicator}>
                              {sortConfig.key === col.key
                                ? sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
                                : ' ⇅'}
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, idx) => (
                    <tr key={idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.tdIndex}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                      {report.columns.map(col => (
                        <td key={col.key} style={styles.td}>
                          {col.key === 'status' ? (
                            <span style={{
                              ...styles.statusBadge,
                              background: getStatusColor(row[col.key]).bg,
                              color: getStatusColor(row[col.key]).text,
                            }}>
                              {row[col.key]}
                            </span>
                          ) : (
                            row[col.key] ?? '—'
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={styles.pageBtn}
                >
                  ← Prev
                </button>
                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let page;
                    if (totalPages <= 7) {
                      page = i + 1;
                    } else if (currentPage <= 4) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      page = totalPages - 6 + i;
                    } else {
                      page = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          ...styles.pageNum,
                          background: currentPage === page ? '#1e3a8a' : 'transparent',
                          color: currentPage === page ? '#fff' : '#64748b',
                          fontWeight: currentPage === page ? '700' : '500',
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={styles.pageBtn}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* Status badge colors */
const getStatusColor = (status) => {
  const s = String(status).toLowerCase();
  if (s === 'active' || s === 'published' || s === 'completed') return { bg: '#dcfce7', text: '#16a34a' };
  if (s === 'inactive' || s === 'dropped' || s === 'draft') return { bg: '#fee2e2', text: '#dc2626' };
  if (s === 'current' || s === 'in-progress') return { bg: '#dbeafe', text: '#2563eb' };
  return { bg: '#f1f5f9', text: '#64748b' };
};

/* ─── Styles ─── */
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    animation: 'fadeIn 0.3s ease',
  },
  selectorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  selectorCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '20px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '14px',
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    fontFamily: 'inherit',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
    gap: '16px',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  panelTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  recordCount: {
    background: '#f1f5f9',
    color: '#64748b',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: '#f8fafc',
    color: '#334155',
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: 'inherit',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'auto',
    minWidth: '130px',
  },
  clearRangeBtn: {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: '#f8fafc',
    color: '#334155',
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: 'inherit',
    cursor: 'pointer',
    minWidth: '130px',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '0 12px',
    gap: '6px',
    transition: 'border-color 0.2s',
  },
  searchIcon: { fontSize: '14px', opacity: 0.5 },
  searchInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '8px 0',
    fontSize: '14px',
    color: '#334155',
    width: '180px',
    fontFamily: 'inherit',
  },
  clearBtn: {
    border: 'none',
    background: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '2px',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  thIndex: {
    background: '#1e293b',
    color: '#94a3b8',
    padding: '12px 14px',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    whiteSpace: 'nowrap',
    width: '50px',
  },
  th: {
    background: '#1e293b',
    color: '#e2e8f0',
    padding: '12px 14px',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    whiteSpace: 'nowrap',
  },
  thContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  sortIndicator: {
    fontSize: '10px',
    opacity: 0.7,
  },
  trEven: { background: '#fff' },
  trOdd: { background: '#f8fafc' },
  tdIndex: {
    padding: '10px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#94a3b8',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '500',
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
    display: 'inline-block',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 24px',
    gap: '8px',
    borderTop: '1px solid #f1f5f9',
  },
  pageBtn: {
    padding: '6px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: '#fff',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  pageNumbers: {
    display: 'flex',
    gap: '4px',
  },
  pageNum: {
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: '#64748b',
    gap: '16px',
    fontSize: '15px',
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#1e3a8a',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 20px',
    gap: '12px',
    color: '#dc2626',
    textAlign: 'center',
  },
  retryBtn: {
    padding: '8px 20px',
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontFamily: 'inherit',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '80px 20px',
    textAlign: 'center',
  },
};

export default AdminReports;
